/**
 * Карточка общего товара в ресурсе — то, по чему приложение находит запасное фото товара. Вид ресурса Platform не
 * разбирает: адрес фото даёт пакет вида через реестр приложения.
 */
export interface ProductRef {
  resourceId: string
  resourceKind: string
  remoteKey: string
  /** `deleted` — карточки в ресурсе уже нет; `null` — Platform состояния не видела. */
  state: string | null
}

/** Адрес главного фото карточки товара в ресурсе; `null` — у вида фото нет. */
export type ProductPhotoUrl = (ref: ProductRef) => string | null

/** Вид обложки общего товара (PHI-13b): фотография реестра фотографий либо временная загрузка («ещё нигде не лежит»). */
export type MainPhotoKind = 'registry' | 'staging'

/**
 * Обложка ОБЩЕГО товара в ответе сервера (`mainPhoto` у позиции поиска и у строки общего каталога, RP-FB70, PHI-13b). По виду, идентификатору и идентификатору
 * товара строится адрес миниатюры (`commonPhotoThumbUrl`); `registry` — `photoId` это ключ фотографии, `staging` — идентификатор загрузки; `null` — у общего товара фото нет.
 */
export interface MainPhotoRef {
  photoId: string
  kind: MainPhotoKind
}

/**
 * Что показать миниатюрой: главное фото ОБЩЕГО товара; только когда его нет — фото карточки источника (запасное). Так миниатюра в выдаче,
 * сканере и общем каталоге берётся у нашего сервера, а не у первой попавшейся карточки 1С.
 */
export function thumbOf(commonPhotoUrl: string | null | undefined, refs: readonly ProductRef[], pick: ProductPhotoUrl | undefined): string | null {
  return commonPhotoUrl ?? firstOf(refs, pick)
}

/**
 * Открыть ОБЩУЮ карточку товара (окно «Общий товар») по идентификатору товара общего каталога. Товар, найденный в общем каталоге
 * (поиск по фото, сканер), открывается только так: карточка источника (1С, AQSI, своя база) открывается из вкладки самого
 * источника, а не из общего поиска (решение владельца 24.10.2026, RP-FB72).
 */
export type OpenCommonProduct = (productId: string) => void

/** Первое, что нашлось по карточкам товара: у товара одно главное фото и одна карточка на ресурс. */
export function firstOf<T>(refs: readonly ProductRef[], pick: ((ref: ProductRef) => T | null) | undefined): T | null {
  if (pick === undefined) return null
  for (const ref of refs) {
    const found = pick(ref)
    if (found !== null) return found
  }
  return null
}
